import i18n from 'i18next'
import store from '../store'
import axiosInstance from '../../axios/axiosInstance'
import {
  SET_PREFERRED_STORY_LEVEL,
  SET_STORIES_BY_LEVEL,
  SET_STORIES_BY_LEVEL_LOADING,
} from '../types'

const PREFERRED_LEVEL_SYNC_STORAGE_KEY = 'preferredStoryLevelSync'

export interface PreferredLevelSyncState {
  level: StoryDifficultyLevel
  expectedStoryId: string | null
  updatedAt: number
}

export enum StoryDifficultyLevel {
  BEGINNER_1 = 'BEGINNER_1',
  BEGINNER_2 = 'BEGINNER_2',
  INTERMEDIATE_1 = 'INTERMEDIATE_1',
  INTERMEDIATE_2 = 'INTERMEDIATE_2',
  ADVANCED_1 = 'ADVANCED_1',
  ADVANCED_2 = 'ADVANCED_2',
  MASTER = 'MASTER',
}

export interface StoryByLevel {
  id: string
  prismic_id: string
  prismic_uid: string
  unique_word_count: number
  newWords: number | null
  knownWords: number | null
  level: StoryDifficultyLevel
  level_display: string
  category: string
  title: any[]
  english_title: any[]
  last_publication_date: string
  image: any
}

export interface LevelOption {
  value: StoryDifficultyLevel
  /** In the interface language, translated each time it is read. */
  readonly label: string
  readonly category: string
  readonly description: string
}

// The copy lives in `common` (the one namespace always loaded), and each getter translates when
// it is read, so every screen that lists levels shows them in the interface language.
const levelOption = (
  value: StoryDifficultyLevel,
  keys: { label: string; category: string; description: string },
): LevelOption => ({
  value,
  get label() {
    return i18n.t(keys.label)
  },
  get category() {
    return i18n.t(keys.category)
  },
  get description() {
    return i18n.t(keys.description)
  },
})

export const LEVEL_OPTIONS: LevelOption[] = [
  levelOption(StoryDifficultyLevel.BEGINNER_1, {
    label: 'common:levels.beginner1.label',
    category: 'common:levelCategories.beginner',
    description: 'common:levels.beginner1.description',
  }),
  levelOption(StoryDifficultyLevel.BEGINNER_2, {
    label: 'common:levels.beginner2.label',
    category: 'common:levelCategories.beginner',
    description: 'common:levels.beginner2.description',
  }),
  levelOption(StoryDifficultyLevel.INTERMEDIATE_1, {
    label: 'common:levels.intermediate1.label',
    category: 'common:levelCategories.intermediate',
    description: 'common:levels.intermediate1.description',
  }),
  levelOption(StoryDifficultyLevel.INTERMEDIATE_2, {
    label: 'common:levels.intermediate2.label',
    category: 'common:levelCategories.intermediate',
    description: 'common:levels.intermediate2.description',
  }),
  levelOption(StoryDifficultyLevel.ADVANCED_1, {
    label: 'common:levels.advanced1.label',
    category: 'common:levelCategories.advanced',
    description: 'common:levels.advanced1.description',
  }),
  levelOption(StoryDifficultyLevel.ADVANCED_2, {
    label: 'common:levels.advanced2.label',
    category: 'common:levelCategories.advanced',
    description: 'common:levels.advanced2.description',
  }),
  levelOption(StoryDifficultyLevel.MASTER, {
    label: 'common:levels.master.label',
    category: 'common:levelCategories.master',
    description: 'common:levels.master.description',
  }),
]

export const getStoriesByLevel = async (level: StoryDifficultyLevel, limit = 5) => {
  try {
    store.dispatch({ type: SET_STORIES_BY_LEVEL_LOADING, payload: true })
    const response = await axiosInstance.get(`stories/by-level`, {
      params: { level, limit },
    })
    store.dispatch({ type: SET_STORIES_BY_LEVEL, payload: response.data })
    store.dispatch({ type: SET_STORIES_BY_LEVEL_LOADING, payload: false })
    return response.data
  } catch (error) {
    store.dispatch({ type: SET_STORIES_BY_LEVEL_LOADING, payload: false })
    console.error('Error fetching stories by level:', error)
    return []
  }
}

export const getPreferredLevelSyncState = (): PreferredLevelSyncState | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const rawValue = localStorage.getItem(PREFERRED_LEVEL_SYNC_STORAGE_KEY)
  if (!rawValue) {
    return null
  }

  try {
    return JSON.parse(rawValue) as PreferredLevelSyncState
  } catch {
    localStorage.removeItem(PREFERRED_LEVEL_SYNC_STORAGE_KEY)
    return null
  }
}

export const clearPreferredLevelSyncState = () => {
  if (typeof window === 'undefined') {
    return
  }
  localStorage.removeItem(PREFERRED_LEVEL_SYNC_STORAGE_KEY)
}

export const updatePreferredStoryLevel = async (level: StoryDifficultyLevel, languageId: string) => {
  try {
    const response = await axiosInstance.patch(`user-language/preferred-level`, {
      level,
      languageId,
    })

    const expectedStoryId = response?.data?.recommended_story_prismic_id || null
    if (typeof window !== 'undefined') {
      const nextSyncState: PreferredLevelSyncState = {
        level,
        expectedStoryId,
        updatedAt: Date.now(),
      }
      localStorage.setItem(PREFERRED_LEVEL_SYNC_STORAGE_KEY, JSON.stringify(nextSyncState))
    }

    store.dispatch({ type: SET_PREFERRED_STORY_LEVEL, payload: level })
    return response.data
  } catch (error) {
    console.error('Error updating preferred story level:', error)
    throw error
  }
}

export const getPreferredStoryLevel = async (languageId: string) => {
  try {
    const response = await axiosInstance.get(`user-language/preferred-level`, {
      params: { languageId },
    })
    const preferredLevel = response.data?.preferred_story_level
    if (preferredLevel) {
      store.dispatch({ type: SET_PREFERRED_STORY_LEVEL, payload: preferredLevel })
    }
    return preferredLevel
  } catch (error) {
    console.error('Error fetching preferred story level:', error)
    return null
  }
}
